import React, { useEffect, useState } from "react";
import axios from "../../utils/axiosConfig";
import { useAuth } from "../../context/AuthContext";
import UserEditDialog from "./UserEditDialog";
import UserAddDialog from "./UserAddDialog";
import { toast } from "../../utils/toast";

import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Tooltip,
  Switch,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControlLabel,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridToolbar,
  GridRenderCellParams
} from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import LockResetIcon from "@mui/icons-material/LockReset";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

interface User {
  _id: string;
  name: string;
  email: string;
  role: 'user' | 'admin' | 'organizer' | 'helper';
  isApproved: boolean;
  isDeactivated?: boolean;
  club?: {
    _id: string;
    name: string;
  };
}

const UsersManagementPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { userRole } = useAuth();

  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [addDialogOpen, setAddDialogOpen] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [showDeactivated, setShowDeactivated] = useState<boolean>(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/admin/users/all");
      setUsers(response.data);
      setError(null);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Fehler beim Laden der Benutzer.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setEditDialogOpen(true);
  };

  const handleUserUpdated = (updatedUser: User) => {
    setUsers(prevUsers =>
      prevUsers.map(user =>
        user._id === updatedUser._id ? updatedUser : user
      )
    );
    toast.success("Benutzer erfolgreich aktualisiert.");
  };

  const handleApprovalToggle = async (user: User) => {
    try {
      if (user.isApproved) {
        await axios.patch(`/api/admin/disapprove/${user._id}`);
        toast.success("Benutzerfreigabe erfolgreich entfernt.");
      } else {
        await axios.patch(`/api/admin/approve/${user._id}`);
        toast.success("Benutzer erfolgreich freigeschaltet.");
      }

      setUsers(prevUsers =>
        prevUsers.map(u =>
          u._id === user._id ? { ...u, isApproved: !u.isApproved } : u
        )
      );
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Fehler beim Ändern des Freigabestatus.";
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    setDeleteLoading(true);
    try {
      const response = await axios.delete(`/api/admin/users/${userToDelete._id}`);
      if (response.data.action === 'deleted') {
        setUsers(prev => prev.filter(u => u._id !== userToDelete._id));
        toast.success("Benutzer wurde gelöscht.");
      } else {
        setUsers(prev => prev.map(u =>
          u._id === userToDelete._id ? { ...u, isDeactivated: true, isApproved: false } : u
        ));
        toast.success("Benutzer wurde deaktiviert (noch als Kontaktperson referenziert).");
      }
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Fehler beim Löschen.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleReactivate = async (user: User) => {
    try {
      await axios.patch(`/api/admin/users/${user._id}/reactivate`);
      setUsers(prev => prev.map(u =>
        u._id === user._id ? { ...u, isDeactivated: false } : u
      ));
      toast.success("Benutzer wurde reaktiviert. Bitte Freigabe erteilen.");
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Fehler beim Reaktivieren.");
    }
  };

  const getRoleChip = (role: string) => {
    let color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" = "default";
    let label = role;

    switch (role) {
      case "admin":
        color = "error";
        label = "Administrator";
        break;
      case "organizer":
        color = "warning";
        label = "Organisator";
        break;
      case "helper":
        color = "success";
        label = "Orga-Helfer";
        break;
      case "user":
        color = "info";
        label = "Benutzer";
        break;
    }

    return <Chip label={label} color={color} size="small" />;
  };

  const displayedUsers = showDeactivated ? users : users.filter(u => !u.isDeactivated);

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams<any, User>) => {
        const user = params.row;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color={user.isDeactivated ? "disabled" : "primary"} fontSize="small" />
            <Typography color={user.isDeactivated ? "text.disabled" : "inherit"}>
              {user.name}
            </Typography>
            {user.isDeactivated && (
              <Chip label="Deaktiviert" size="small" color="default" sx={{ ml: 0.5 }} />
            )}
          </Box>
        );
      }
    },
    {
      field: "email",
      headerName: "E-Mail",
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<any, User>) => {
        const user = params.row;
        return (
          <Tooltip title={user.email} placement="top-start">
            <Typography
              color={user.isDeactivated ? "text.disabled" : "inherit"}
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {user.email}
            </Typography>
          </Tooltip>
        );
      }
    },
    {
      field: "club",
      headerName: "Verein",
      flex: 1,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams<any, User>) => {
        const user = params.row;
        return (
          <Typography color={user.isDeactivated ? "text.disabled" : "inherit"}>
            {user.club ? user.club.name : "Kein Verein"}
          </Typography>
        );
      }
    },
    {
      field: "role",
      headerName: "Rolle",
      flex: 0.8,
      minWidth: 130,
      renderCell: (params: GridRenderCellParams<any, User>) => {
        const user = params.row;
        return getRoleChip(user.role);
      }
    },
    {
      field: "isApproved",
      headerName: "Freigegeben",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<any, User>) => {
        const user = params.row;
        return (
          <Switch
            checked={user.isApproved}
            onChange={() => handleApprovalToggle(user)}
            color="primary"
            disabled={!!user.isDeactivated}
          />
        );
      }
    },
    {
      field: "actions",
      headerName: "Aktionen",
      flex: 1,
      minWidth: 280,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any, User>) => {
        const user = params.row;
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => handleEdit(user)}
              disabled={!!user.isDeactivated}
            >
              Bearbeiten
            </Button>
            <Button
              variant="outlined"
              color="warning"
              size="small"
              startIcon={<LockResetIcon />}
              disabled={true}
              title="Funktion noch nicht verfügbar"
            >
              Passwort
            </Button>
            {user.isDeactivated ? (
              <Button
                variant="outlined"
                color="success"
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={() => handleReactivate(user)}
              >
                Reaktivieren
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={() => handleDeleteClick(user)}
              >
                Löschen
              </Button>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Container component="main" maxWidth="xl">
      <Paper elevation={3} sx={{ padding: 3, mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">
            Benutzerverwaltung
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showDeactivated}
                  onChange={(e) => setShowDeactivated(e.target.checked)}
                  size="small"
                />
              }
              label="Deaktivierte anzeigen"
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
            >
              Neuen Benutzer anlegen
            </Button>
          </Box>
        </Box>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Box sx={{ height: 'auto', width: "100%", mt: 2 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && (
            <DataGrid
              rows={displayedUsers}
              columns={columns}
              getRowId={(row) => row._id}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 },
                },
              }}
              autoHeight
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 300 },
                },
              }}
              getRowClassName={(params) => params.row.isDeactivated ? 'deactivated-row' : ''}
              sx={{
                '& .MuiDataGrid-cell': { py: 1 },
                '& .MuiDataGrid-columnHeaders': { backgroundColor: 'background.default' },
                '& .deactivated-row': { opacity: 0.5 },
              }}
            />
          )}
        </Box>
      </Paper>

      {/* Dialog zum Bearbeiten eines Benutzers */}
      <UserEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        user={selectedUser}
        onUserUpdated={handleUserUpdated}
      />

      {/* Dialog zum Hinzufügen eines Benutzers */}
      <UserAddDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onUserAdded={fetchUsers}
      />

      {/* Bestätigungsdialog Löschen */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Benutzer löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie den Benutzer <strong>{userToDelete?.name}</strong> wirklich löschen?
            <br /><br />
            Falls der Benutzer noch als Kontaktperson oder Organisator referenziert ist, wird er nur <strong>deaktiviert</strong> und kann sich nicht mehr einloggen. Andernfalls wird er dauerhaft gelöscht.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Abbrechen
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={deleteLoading} variant="contained">
            {deleteLoading ? <CircularProgress size={20} /> : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UsersManagementPage;
